/* tslint:disable */
/* eslint-disable */
import { HttpClient, HttpContext } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { BaseService } from '../base-service';
import { ApiConfiguration } from '../api-configuration';
import { StrictHttpResponse } from '../strict-http-response';

import { ApiResponseOfString } from '../models/api-response-of-string';
import { DataPageOfEhrUsers } from '../models/data-page-of-ehr-users';
import { DataPageOfUser } from '../models/data-page-of-user';
import { User } from '../models/user';
import { userChangePassword } from '../fn/user/user-change-password';
import { UserChangePassword$Params } from '../fn/user/user-change-password';
import { userDelete } from '../fn/user/user-delete';
import { UserDelete$Params } from '../fn/user/user-delete';
import { userEcho } from '../fn/user/user-echo';
import { UserEcho$Params } from '../fn/user/user-echo';
import { userGet } from '../fn/user/user-get';
import { UserGet$Params } from '../fn/user/user-get';
import { userGetEhrUserSearch } from '../fn/user/user-get-ehr-user-search';
import { UserGetEhrUserSearch$Params } from '../fn/user/user-get-ehr-user-search';
import { userLock } from '../fn/user/user-lock';
import { UserLock$Params } from '../fn/user/user-lock';
import { userPost } from '../fn/user/user-post';
import { UserPost$Params } from '../fn/user/user-post';
import { userPut } from '../fn/user/user-put';
import { UserPut$Params } from '../fn/user/user-put';
import { userRemove2Fa } from '../fn/user/user-remove-2-fa';
import { UserRemove2Fa$Params } from '../fn/user/user-remove-2-fa';
import { userResetAllPassword } from '../fn/user/user-reset-all-password';
import { UserResetAllPassword$Params } from '../fn/user/user-reset-all-password';
import { userResetForgotPassword } from '../fn/user/user-reset-forgot-password';
import { UserResetForgotPassword$Params } from '../fn/user/user-reset-forgot-password';
import { userSearch } from '../fn/user/user-search';
import { UserSearch$Params } from '../fn/user/user-search';
import { userUnLock } from '../fn/user/user-un-lock';
import { UserUnLock$Params } from '../fn/user/user-un-lock';
import { userViewprofile } from '../fn/user/user-viewprofile';
import { UserViewprofile$Params } from '../fn/user/user-viewprofile';

@Injectable({ providedIn: 'root' })
export class UserService extends BaseService {
  constructor(config: ApiConfiguration, http: HttpClient) {
    super(config, http);
  }

  /** Path part for operation `userEcho()` */
  static readonly UserEchoPath = '/api/users/echo';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `userEcho()` instead.
   *
   * This method doesn't expect any request body.
   */
  userEcho$Response(params?: UserEcho$Params, context?: HttpContext): Observable<StrictHttpResponse<Blob>> {
    return userEcho(this.http, this.rootUrl, params, context);
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `userEcho$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  userEcho(params?: UserEcho$Params, context?: HttpContext): Observable<Blob> {
    return this.userEcho$Response(params, context).pipe(
      map((r: StrictHttpResponse<Blob>): Blob => r.body)
    );
  }

  /** Path part for operation `userGet()` */
  static readonly UserGetPath = '/api/practices/{practiceId}/users/{id}';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `userGet()` instead.
   *
   * This method doesn't expect any request body.
   */
  userGet$Response(params: UserGet$Params, context?: HttpContext): Observable<StrictHttpResponse<User>> {
    return userGet(this.http, this.rootUrl, params, context);
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `userGet$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  userGet(params: UserGet$Params, context?: HttpContext): Observable<User> {
    return this.userGet$Response(params, context).pipe(
      map((r: StrictHttpResponse<User>): User => r.body)
    );
  }

  /** Path part for operation `userSearch()` */
  static readonly UserSearchPath = '/api/practices/{practiceId}/users';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `userSearch()` instead.
   *
   * This method doesn't expect any request body.
   */
  userSearch$Response(params: UserSearch$Params, context?: HttpContext): Observable<StrictHttpResponse<DataPageOfUser>> {
    return userSearch(this.http, this.rootUrl, params, context);
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `userSearch$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  userSearch(params: UserSearch$Params, context?: HttpContext): Observable<DataPageOfUser> {
    return this.userSearch$Response(params, context).pipe(
      map((r: StrictHttpResponse<DataPageOfUser>): DataPageOfUser => r.body)
    );
  }

  /** Path part for operation `userPost()` */
  static readonly UserPostPath = '/api/practices/{practiceId}/users';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `userPost()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  userPost$Response(params: UserPost$Params, context?: HttpContext): Observable<StrictHttpResponse<ApiResponseOfString>> {
    return userPost(this.http, this.rootUrl, params, context);
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `userPost$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  userPost(params: UserPost$Params, context?: HttpContext): Observable<ApiResponseOfString> {
    return this.userPost$Response(params, context).pipe(
      map((r: StrictHttpResponse<ApiResponseOfString>): ApiResponseOfString => r.body)
    );
  }

  /** Path part for operation `userPut()` */
  static readonly UserPutPath = '/api/practices/{practiceId}/users/{userId}';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `userPut()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  userPut$Response(params: UserPut$Params, context?: HttpContext): Observable<StrictHttpResponse<void>> {
    return userPut(this.http, this.rootUrl, params, context);
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `userPut$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  userPut(params: UserPut$Params, context?: HttpContext): Observable<void> {
    return this.userPut$Response(params, context).pipe(
      map((r: StrictHttpResponse<void>): void => r.body)
    );
  }

  /** Path part for operation `userDelete()` */
  static readonly UserDeletePath = '/api/practices/{practiceId}/users/{userId}';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `userDelete()` instead.
   *
   * This method doesn't expect any request body.
   */
  userDelete$Response(params: UserDelete$Params, context?: HttpContext): Observable<StrictHttpResponse<Blob>> {
    return userDelete(this.http, this.rootUrl, params, context);
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `userDelete$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  userDelete(params: UserDelete$Params, context?: HttpContext): Observable<Blob> {
    return this.userDelete$Response(params, context).pipe(
      map((r: StrictHttpResponse<Blob>): Blob => r.body)
    );
  }

  /** Path part for operation `userChangePassword()` */
  static readonly UserChangePasswordPath = '/user/changepassword';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `userChangePassword()` instead.
   *
   * This method doesn't expect any request body.
   */
  userChangePassword$Response(params?: UserChangePassword$Params, context?: HttpContext): Observable<StrictHttpResponse<ApiResponseOfString>> {
    return userChangePassword(this.http, this.rootUrl, params, context);
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `userChangePassword$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  userChangePassword(params?: UserChangePassword$Params, context?: HttpContext): Observable<ApiResponseOfString> {
    return this.userChangePassword$Response(params, context).pipe(
      map((r: StrictHttpResponse<ApiResponseOfString>): ApiResponseOfString => r.body)
    );
  }

  /** Path part for operation `userResetAllPassword()` */
  static readonly UserResetAllPasswordPath = '/user/resetallpassword';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `userResetAllPassword()` instead.
   *
   * This method doesn't expect any request body.
   */
  userResetAllPassword$Response(params?: UserResetAllPassword$Params, context?: HttpContext): Observable<StrictHttpResponse<ApiResponseOfString>> {
    return userResetAllPassword(this.http, this.rootUrl, params, context);
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `userResetAllPassword$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  userResetAllPassword(params?: UserResetAllPassword$Params, context?: HttpContext): Observable<ApiResponseOfString> {
    return this.userResetAllPassword$Response(params, context).pipe(
      map((r: StrictHttpResponse<ApiResponseOfString>): ApiResponseOfString => r.body)
    );
  }

  /** Path part for operation `userLock()` */
  static readonly UserLockPath = '/user/Lock';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `userLock()` instead.
   *
   * This method doesn't expect any request body.
   */
  userLock$Response(params?: UserLock$Params, context?: HttpContext): Observable<StrictHttpResponse<Blob>> {
    return userLock(this.http, this.rootUrl, params, context);
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `userLock$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  userLock(params?: UserLock$Params, context?: HttpContext): Observable<Blob> {
    return this.userLock$Response(params, context).pipe(
      map((r: StrictHttpResponse<Blob>): Blob => r.body)
    );
  }

  /** Path part for operation `userUnLock()` */
  static readonly UserUnLockPath = '/user/UnLock';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `userUnLock()` instead.
   *
   * This method doesn't expect any request body.
   */
  userUnLock$Response(params?: UserUnLock$Params, context?: HttpContext): Observable<StrictHttpResponse<Blob>> {
    return userUnLock(this.http, this.rootUrl, params, context);
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `userUnLock$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  userUnLock(params?: UserUnLock$Params, context?: HttpContext): Observable<Blob> {
    return this.userUnLock$Response(params, context).pipe(
      map((r: StrictHttpResponse<Blob>): Blob => r.body)
    );
  }

  /** Path part for operation `userGetEhrUserSearch()` */
  static readonly UserGetEhrUserSearchPath = '/user/EhruserSearch';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `userGetEhrUserSearch()` instead.
   *
   * This method doesn't expect any request body.
   */
  userGetEhrUserSearch$Response(params?: UserGetEhrUserSearch$Params, context?: HttpContext): Observable<StrictHttpResponse<DataPageOfEhrUsers>> {
    return userGetEhrUserSearch(this.http, this.rootUrl, params, context);
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `userGetEhrUserSearch$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  userGetEhrUserSearch(params?: UserGetEhrUserSearch$Params, context?: HttpContext): Observable<DataPageOfEhrUsers> {
    return this.userGetEhrUserSearch$Response(params, context).pipe(
      map((r: StrictHttpResponse<DataPageOfEhrUsers>): DataPageOfEhrUsers => r.body)
    );
  }

  /** Path part for operation `userViewprofile()` */
  static readonly UserViewprofilePath = '/api/user/profile';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `userViewprofile()` instead.
   *
   * This method doesn't expect any request body.
   */
  userViewprofile$Response(params?: UserViewprofile$Params, context?: HttpContext): Observable<StrictHttpResponse<User>> {
    return userViewprofile(this.http, this.rootUrl, params, context);
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `userViewprofile$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  userViewprofile(params?: UserViewprofile$Params, context?: HttpContext): Observable<User> {
    return this.userViewprofile$Response(params, context).pipe(
      map((r: StrictHttpResponse<User>): User => r.body)
    );
  }

  /** Path part for operation `userResetForgotPassword()` */
  static readonly UserResetForgotPasswordPath = '/user/resetlforgotpassword';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `userResetForgotPassword()` instead.
   *
   * This method doesn't expect any request body.
   */
  userResetForgotPassword$Response(params?: UserResetForgotPassword$Params, context?: HttpContext): Observable<StrictHttpResponse<ApiResponseOfString>> {
    return userResetForgotPassword(this.http, this.rootUrl, params, context);
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `userResetForgotPassword$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  userResetForgotPassword(params?: UserResetForgotPassword$Params, context?: HttpContext): Observable<ApiResponseOfString> {
    return this.userResetForgotPassword$Response(params, context).pipe(
      map((r: StrictHttpResponse<ApiResponseOfString>): ApiResponseOfString => r.body)
    );
  }

  /** Path part for operation `userRemove2Fa()` */
  static readonly UserRemove2FaPath = '/user/remove2fa';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `userRemove2Fa()` instead.
   *
   * This method doesn't expect any request body.
   */
  userRemove2Fa$Response(params?: UserRemove2Fa$Params, context?: HttpContext): Observable<StrictHttpResponse<Blob>> {
    return userRemove2Fa(this.http, this.rootUrl, params, context);
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `userRemove2Fa$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  userRemove2Fa(params?: UserRemove2Fa$Params, context?: HttpContext): Observable<Blob> {
    return this.userRemove2Fa$Response(params, context).pipe(
      map((r: StrictHttpResponse<Blob>): Blob => r.body)
    );
  }

}
